/**
 * Hook for filtering structure tree based on search term
 */
import { useState, useMemo } from "react"
import { StructureNode } from "utils/structureParser"

interface UseTreeFilterReturn {
  searchTerm: string
  setSearchTerm: (term: string) => void
  filteredTree: StructureNode | null
}

/**
 * Custom hook to handle tree filtering logic
 * @param structureTree - The root structure tree node
 * @returns Filtered tree and search state
 */
const filterTree = (node: StructureNode, term: string): StructureNode | null => {
  const normalizedTerm = term.toLowerCase()
  const nodeMatches = node.name.toLowerCase().includes(normalizedTerm)

  const filteredChildren = node.children
    ? node.children
        .map((child) => filterTree(child, term))
        .filter((child): child is StructureNode => child !== null)
    : []

  if (nodeMatches || filteredChildren.length > 0) {
    return {
      ...node,
      children: filteredChildren.length > 0 ? filteredChildren : node.children,
    }
  }

  return null
}

export const useTreeFilter = (structureTree: StructureNode | null): UseTreeFilterReturn => {
  const [searchTerm, setSearchTerm] = useState("")

  const filteredTree = useMemo(() => {
    if (!structureTree) return null
    if (!searchTerm.trim()) return structureTree

    return filterTree(structureTree, searchTerm)
  }, [structureTree, searchTerm])

  return {
    searchTerm,
    setSearchTerm,
    filteredTree,
  }
}
